import { commerceCartIdSchema } from '../commerceAnalytics'
import { z } from 'zod'
import { manufacturerGuaranteeSnapshotSchema } from '../manufacturerGuarantees'
import typeguard from '../typeguard'

import { imageSrcSchema } from '../frontendTypes/images'
import { couponSchema } from '../frontendTypes/coupon'
import { shopId, isShopId } from '../shops'
import { productPurchaseModeSchema } from '../productPurchaseMode'
//import { OnApproveData } from '@paypal/paypal-js/types/components/buttons'

import { productSchema } from './product'
import { combinationSchema } from './combination'
import { discountPriceSchema } from '../frontendTypes/product'
import { ebayAccountKeySchema } from '../ebayAccounts'

export const shippingTypeSchema = z.enum(['dhl', 'spedition'])
export type shippingType = z.infer<typeof shippingTypeSchema>


export const paymentTypeSchema = process.env.NODE_ENV === 'test'
  ? z.enum([
    'vorkasse',
    'nachnahme',
    'rechnung',
    'demo',
    'paypal',
    'ebay',
    'none',
  ])
  : z.enum([
    'vorkasse',
    'nachnahme',
    'rechnung',
    'demo',
    'paypal',
    'ebay',
    'none',
  ])
export type paymentType = z.infer<typeof paymentTypeSchema>

export const paypalPaymentSourceSchema = z.string()
  .trim()
  .regex(/^[a-z0-9_-]{1,64}$/)
export type paypalPaymentSource = z.infer<typeof paypalPaymentSourceSchema>

export const orderTrafficAttributionSchema = z.object({
  clientIp: z.string().optional(),
  ipHash: z.string().optional(),
  ipPrefix: z.string().optional(),
  country: z.string().optional(),
  host: z.string().optional(),
  referrerSource: z.string().optional(),
  sourceType: z.string().optional(),
  referrerHost: z.string().optional(),
  landingPath: z.string().optional(),
  trafficFirstSeenAt: z.date().optional(),
  trafficLastSeenAt: z.date().optional(),
  matchedAt: z.date().optional(),
  lookbackDays: z.number().optional(),
  landingClickCount: z.number().optional(),
  pageCount: z.number().optional(),
})
export type OrderTrafficAttribution = z.infer<typeof orderTrafficAttributionSchema>


export const adresseSchema = z.object({
  firma: z.string().optional(),
  anrede: z.string().optional(),
  vorname: z.string(),
  nachname: z.string(),
  strasse: z.string(),
  nr: z.string(),
  adresse2: z.string().optional(),
  plz: z.string(),
  stadt: z.string(),
  land: z.string(),
  email: z.string(),
  telefon: z.string().optional(),
  
  kundennr: z.string().optional(),
  postnummer: z.string().optional(),
  packstation: z.string().optional(),
  postfiliale: z.string().optional(),
})
export type Adresse = z.infer<typeof adresseSchema>

export const adresseShippingSchema = adresseSchema.extend({
  vorname: z.string().optional(),
  nachname: z.string().optional(),
  strasse: z.string().optional(),
  nr: z.string().optional(),
  email: z.string().optional(),
})
export type AdresseShipping = z.infer<typeof adresseShippingSchema>


export const attributesSchema = z.record(z.number())
export type attributes = z.infer<typeof attributesSchema>


export const attributesTextSchema = z.array(z.object({
  titel: z.string(),
  option: z.string(),
}))
export type attributesText = z.infer<typeof attributesTextSchema>

export const priceLockSchema = z.object({
  price: z.union([
    z.number(),
    discountPriceSchema,
  ]),
  createdAt: z.string(),
  expiresAt: z.string(),
  hash: z.string(),
})
export type PriceLock = z.infer<typeof priceLockSchema>

export const orderItemFulfillmentStatusSchema = z.enum([
  'unknown',
  'open',
  'not_ordered',
  'ordered_unconfirmed',
  'ordered_confirmed',
  'waiting_for_stock',
  'in_stock',
  'partially_in_stock',
  'partially_shipped',
  'partially_cancelled',
  'shipped',
  'cancelled',
  'no_longer_available',
])
export type OrderItemFulfillmentStatus = z.infer<typeof orderItemFulfillmentStatusSchema>

export const orderItemFulfillmentSchema = z.object({
  status: orderItemFulfillmentStatusSchema.optional(),
  stockQty: z.number().optional(),
  shippedQty: z.number().optional(),
  cancelledQty: z.number().optional(),
  expectedDeliveryDate: z.date().optional(),
  expectedDeliveryText: z.string().optional(),
  note: z.string().optional(),
  updatedAt: z.date().optional(),
  updatedBy: z.string().optional(),
})
export type OrderItemFulfillment = z.infer<typeof orderItemFulfillmentSchema>

export const itemSchema = z.object({
  id: z.number(),
  categorie: z.number().optional(),
  key: z.string(),
  path: z.string(),
  name: z.string(),
  brand: z.object({
    id: z.number(),
    titel: z.string(),
  }).optional(),
  price: z.number(),
  qty: z.number(),
  attributes: attributesSchema.optional(),
  attributesText: attributesTextSchema.optional(),
  image: imageSrcSchema.optional(),
  deliverytime: z.number().optional(),
  stockDeliverytime: z.number().optional(),
  shippingType: z.number(),
  stock: z.number().optional(),
  onlyStock: z.boolean().optional(),
  hash: z.string().optional(),
  priceLock: priceLockSchema.optional(),
  purchaseMode: productPurchaseModeSchema.optional(),
  inquireOnly: z.boolean().optional(),
  fulfillment: orderItemFulfillmentSchema.optional(),
  manufacturerGuarantees: z.array(manufacturerGuaranteeSnapshotSchema).optional(),
  manufacturerGuaranteeRevisions: z.array(z.string().regex(/^[a-f0-9]{64}$/)).max(10).optional(),

  product: productSchema.optional(),
  combination: combinationSchema.optional(),
})
export type Item = z.infer<typeof itemSchema>


export const warenkorbSchema = z.array(itemSchema)
export type Warenkorb = z.infer<typeof warenkorbSchema>


export const totalsSchema = z.object({
  WarenkorbBrutto: z.number(),
  WarenkorbNetto: z.number(),
  WarenkorbEnthalteneMwSt: z.number(),
  
  Rabatt: z.number().optional(),
  Versandkosten: z.number(),
  Mindermengenzuschlag: z.number().optional(),
  EnthalteneMwSt: z.number(),
  Gesamtsumme: z.number(),
})
export type Totals = z.infer<typeof totalsSchema>


export const OnApproveDataSchema = z.object({
  billingToken: z.string().nullable().optional(),
  facilitatorAccessToken: z.string(),
  orderID: z.string(),
  payerID: z.string().optional(),
  paymentID: z.string().nullable().optional(),
  subscriptionID: z.string().optional(),
  authCode: z.string().optional(),
})

export const shippingAdressType = z.enum([
  'hausanschrift',
  'geschaeft',
  'packstation',
  'postfiliale',
]).default('hausanschrift')
export type ShippingAdressType = z.infer<typeof shippingAdressType>

export const orderSchema = z.object({
  cartId: commerceCartIdSchema.optional(),
  shop: z.custom<shopId>((val)=>isShopId(val)),

  billingAdress: adresseSchema,
  shippingAdress: adresseShippingSchema.optional(),
  shippingAdressType,

  zahlungsart: paymentTypeSchema,
  versandart: shippingTypeSchema,

  warenkorb: warenkorbSchema,
  totals: totalsSchema,

  payPalInfos: OnApproveDataSchema.optional(),
  paypalPaymentSource: paypalPaymentSourceSchema.optional(),
  couponObjekt: couponSchema.nullable().optional(),
})
export type Order = z.infer<typeof orderSchema>
export const isOrder = typeguard<Order>(orderSchema)

export const ordersSchema = z.array(orderSchema)
export type Orders = z.infer<typeof ordersSchema>
export const isOrders = typeguard<Orders>(ordersSchema)


export const statusSchema = z.enum(['open', 'confirmed', 'shipped', 'completed', 'cancelled'])
export type status = z.infer<typeof statusSchema>
export const isStatus = typeguard<status>(statusSchema)

export const orderPaymentStatusSchema = z.enum([
  'unknown',
  'open',
  'paid',
  'partially_paid',
  'refunded',
])
export type OrderPaymentStatus = z.infer<typeof orderPaymentStatusSchema>

export const orderFulfillmentStatusSchema = z.enum([
  'unknown',
  'open',
  'not_ordered',
  'ordered_unconfirmed',
  'ordered_confirmed',
  'waiting_for_stock',
  'withdrawal_received',
  'partially_in_stock',
  'ready_to_ship',
  'partially_shipped',
  'partially_cancelled',
  'shipped',
  'completed',
  'cancelled',
  'no_longer_available',
])
export type OrderFulfillmentStatus = z.infer<typeof orderFulfillmentStatusSchema>

export const orderOperationsSchema = z.object({
  paymentStatus: orderPaymentStatusSchema.optional(),
  paid: z.boolean().optional(),
  paidAt: z.date().optional(),
  paymentMethod: z.string().optional(),
  fulfillmentStatus: orderFulfillmentStatusSchema.optional(),
  expectedDeliveryDate: z.date().optional(),
  customerStatusText: z.string().optional(),
  internalNote: z.string().optional(),
  businessUpdatedAt: z.date().optional(),
  updatedAt: z.date().optional(),
  updatedBy: z.string().optional(),
})
export type OrderOperations = z.infer<typeof orderOperationsSchema>

export const orderTrackingTransferSchema = z.object({
  paypal: z.boolean().default(false),
  ebay: z.boolean().default(false),
})
export type OrderTrackingTransfer = z.infer<typeof orderTrackingTransferSchema>

export const orderTrackingTransferAtSchema = z.object({
  paypal: z.date().optional(),
  ebay: z.date().optional(),
})
export type OrderTrackingTransferAt = z.infer<typeof orderTrackingTransferAtSchema>

export const orderTrackingEbayTransferFailureSchema = z.object({
  status: z.enum(['retryable', 'blocked', 'dismissed']),
  attempts: z.number().int().min(1),
  lastAttemptAt: z.date(),
  nextAttemptAt: z.date().optional(),
  blockedAt: z.date().optional(),
  code: z.string().optional(),
  message: z.string().optional(),
  classification: z.string().optional(),
  ack: z.string().optional(),
  dismissedAt: z.date().optional(),
  dismissedBy: z.string().optional(),
  dismissalReason: z.string().optional(),
})
export type OrderTrackingEbayTransferFailure = z.infer<
  typeof orderTrackingEbayTransferFailureSchema
>

export const orderTrackingSchema = z.object({
  carrier: z.string(),
  trackingNumber: z.string(),
  trackingUrl: z.string().optional(),
  note: z.string().optional(),
  source: z.string().optional(),
  created: z.date(),
  transferred: orderTrackingTransferSchema,
  transferredAt: orderTrackingTransferAtSchema.optional(),
  ebayTransferFailure: orderTrackingEbayTransferFailureSchema.optional(),
})
export type OrderTracking = z.infer<typeof orderTrackingSchema>


export const OrderSchemaSchema = orderSchema.extend({
  id: z.number(),
  user: z.string().optional(),
  auftragNr: z.number().optional(),
  customerAddressRevision: z.number().int().nonnegative().optional(),
  customerAddressChangedAt: z.date().optional(),
  customerAddressSyncedRevision: z.number().int().nonnegative().optional(),
  customerAddressBusinessReady: z.boolean().optional(),
  customerAddressLock: z.object({ at: z.date(), requestId: z.string(), auftragNr: z.number(), actor: z.string() }).optional(),
  customerAddressChanges: z.array(z.any()).optional(),
  ebayOrderId: z.string().optional(),
  ebayAccountKey: ebayAccountKeySchema.optional(),
  email: z.string(),
  price: z.number(),
  note: z.string().optional(),
  orderDate: z.date(),
  token: z.string(),
  status: statusSchema,
  operations: orderOperationsSchema.optional(),
  paypalId: z.string().optional(),
  paypalPaymentSource: paypalPaymentSourceSchema.optional(),
  trafficAttribution: orderTrafficAttributionSchema.optional(),
  tracking: z.array(orderTrackingSchema).optional(),

  created: z.date(),
  changed: z.date(),
})
export type OrderSchema = z.infer<typeof OrderSchemaSchema>
export const isOrderSchema = typeguard<OrderSchema>(OrderSchemaSchema)

export const OrdersSchemaSchema = z.array(OrderSchemaSchema)
export type OrdersSchema = z.infer<typeof OrdersSchemaSchema>
export const isOrdersSchema = typeguard<OrdersSchema>(OrdersSchemaSchema)

const orderViewDateSchema = z.union([z.date(), z.string()])

const orderViewItemFulfillmentSchema = orderItemFulfillmentSchema.extend({
  expectedDeliveryDate: orderViewDateSchema.optional(),
  updatedAt: orderViewDateSchema.optional(),
})

const orderViewItemSchema = itemSchema.extend({
  fulfillment: orderViewItemFulfillmentSchema.optional(),
})

const orderViewOperationsSchema = orderOperationsSchema.extend({
  paidAt: orderViewDateSchema.optional(),
  expectedDeliveryDate: orderViewDateSchema.optional(),
  businessUpdatedAt: orderViewDateSchema.optional(),
  updatedAt: orderViewDateSchema.optional(),
})

const orderViewTrackingSchema = orderTrackingSchema.extend({
  created: orderViewDateSchema.optional(),
  transferred: orderTrackingTransferSchema.optional(),
  transferredAt: z.object({
    paypal: orderViewDateSchema.optional(),
    ebay: orderViewDateSchema.optional(),
  }).optional(),
})

export const orderViewSchema = OrderSchemaSchema.pick({
  id: true,
  ebayOrderId: true,
  ebayAccountKey: true,
  billingAdress: true,
  shippingAdress: true,
  shippingAdressType: true,
  orderDate: true,
  price: true,
  status: true,
  versandart: true,
  zahlungsart: true,
  totals: true,
}).extend({
  orderDate: z.string(),
  warenkorb: z.array(orderViewItemSchema),
  operations: orderViewOperationsSchema.optional(),
  tracking: z.array(orderViewTrackingSchema).optional(),
})
export type orderView = z.infer<typeof orderViewSchema>
export const isOrderView = typeguard<orderView>(orderViewSchema)

export const orderViewsSchema = z.array(orderViewSchema)
export type orderViews = z.infer<typeof orderViewsSchema>
export const isOrderViews = typeguard<orderViews>(orderViewsSchema)

/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/* Requests */

const orderResponseSchema = z.object({
  id: z.number(),
  token: z.string(),
})
export type orderResponse = z.infer<typeof orderResponseSchema>
export const isNewOrderResponse = typeguard<orderResponse>(orderResponseSchema)


export const orderTokenRequestSchema = z.object({
  id: z.number(),
  token: z.string(),
})
export type orderRequest = z.infer<typeof orderTokenRequestSchema>
export const isOrderTokenRequest = typeguard<orderRequest>(orderTokenRequestSchema)

/* ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
